import React, { Component } from 'react'
import { Popover, PopoverBody } from 'reactstrap'
import {
	NULL_FILTER_VALUE,
	mergeSelectedIntoOptions,
	setColumnFilterSelection,
	toggleFilterValue,
} from '../../utils/tableFilters'

export default class ColumnFilterPopover extends Component {
	constructor(props) {
		super(props)
		this.state = {
			open: false,
			search: '',
			// Draft selection while the popover is open (null when closed / in sync)
			draftSelected: null,
		}
		this.targetId = `column-filter-${props.field}`.replace(/[^a-zA-Z0-9_-]/g, '-')
		this.labelCache = new Map()
		this.isApplying = false
		this.cacheOptionLabels(props.options)
	}

	componentDidUpdate(prevProps) {
		if (prevProps.options !== this.props.options) {
			this.cacheOptionLabels(this.props.options)
		}
	}

	cacheOptionLabels = (options) => {
		;(options || []).forEach((option) => {
			if (option == null || option.value === undefined || option.value === null) {
				return
			}
			this.labelCache.set(String(option.value), String(option.label ?? option.value))
		})
	}

	fieldValues = (filters, field) => {
		if (filters && Array.isArray(filters[field])) {
			return filters[field].map(String)
		}
		return []
	}

	valuesEqual = (a, b) => {
		const left = [...(a || [])].map(String).sort()
		const right = [...(b || [])].map(String).sort()
		if (left.length !== right.length) {
			return false
		}
		return left.every((value, index) => value === right[index])
	}

	getAppliedValues = () => {
		return this.fieldValues(this.props.filters, this.props.field)
	}

	getDraftValues = () => {
		const { draftSelected } = this.state
		if (draftSelected !== null) {
			return draftSelected.map(String)
		}
		return this.getAppliedValues()
	}

	isActive = () => {
		return this.getAppliedValues().length > 0
	}

	hasDraftChanges = () => {
		return !this.valuesEqual(this.getDraftValues(), this.getAppliedValues())
	}

	getDisplayOptions = () => {
		const { options = [] } = this.props
		return mergeSelectedIntoOptions(options, this.getDraftValues(), this.labelCache)
	}

	/**
	 * Close without committing — used for outside-click and filter-button toggle.
	 * Only Apply commits draft changes to the parent.
	 */
	discardDraftAndClose = () => {
		this.isApplying = false
		this.setState({ open: false, search: '', draftSelected: null })
	}

	toggle = (event) => {
		if (event) {
			event.preventDefault()
			event.stopPropagation()
		}
		if (this.state.open) {
			this.discardDraftAndClose()
			return
		}
		this.setState({
			open: true,
			search: '',
			draftSelected: this.getAppliedValues(),
		})
	}

	closePopover = () => {
		this.discardDraftAndClose()
	}

	handleToggleValue = (value) => {
		const { field } = this.props
		const current = { [field]: this.getDraftValues() }
		const next = toggleFilterValue(current, field, value)
		const nextSelected = Array.isArray(next[field]) ? next[field].map(String) : []
		this.setState({ draftSelected: nextSelected })
	}

	clearSelections = (event) => {
		if (event) {
			event.preventDefault()
			event.stopPropagation()
		}
		this.setState({ draftSelected: [] })
	}

	applySelections = (event) => {
		if (event) {
			event.preventDefault()
			event.stopPropagation()
		}
		// Sync guard: setState is async, so a double-click can otherwise commit twice
		if (this.isApplying) {
			return
		}
		const { field, onFiltersChange } = this.props
		if (this.state.draftSelected !== null && this.hasDraftChanges()) {
			this.isApplying = true
			const draft = this.getDraftValues()
			onFiltersChange((prev) => setColumnFilterSelection(prev || {}, field, draft))
		}
		this.setState({ open: false, search: '', draftSelected: null }, () => {
			this.isApplying = false
		})
	}

	render() {
		const { options = [], field } = this.props
		const { open, search } = this.state
		const applied = this.getAppliedValues()
		const selected = this.getDraftValues()
		const active = this.isActive()
		const canApply = this.hasDraftChanges()
		const displayOptions = this.getDisplayOptions()
		const searchLower = search.trim().toLowerCase()
		const visibleOptions = searchLower
			? displayOptions.filter((option) =>
					String(option.label || '').toLowerCase().includes(searchLower)
				)
			: displayOptions
		const showSearch = options.length > 8 || displayOptions.length > 8

		return (
			<span
				className="column-filter-control ml-1"
				onClick={(event) => event.stopPropagation()}
				onMouseDown={(event) => event.stopPropagation()}
			>
				<button
					id={this.targetId}
					type="button"
					className="btn btn-link btn-sm p-0 column-filter-button"
					onClick={this.toggle}
					title={`Filter ${field}`}
					aria-label={`Filter ${field}`}
					style={{
						lineHeight: 1,
						color: active ? '#007bff' : '#6c757d',
						verticalAlign: 'baseline',
						display: 'inline-flex',
						alignItems: 'center',
						gap: '3px',
					}}
				>
					<i className="fas fa-filter" style={{ opacity: active ? 1 : 0.55 }} />
					{active && (
						<span
							className="badge badge-primary"
							style={{
								fontSize: '0.65rem',
								padding: '0.15em 0.4em',
								lineHeight: 1.2,
								verticalAlign: 'middle',
							}}
							aria-label={`${applied.length} selected`}
						>
							{applied.length}
						</span>
					)}
				</button>
				<Popover
					placement="bottom"
					isOpen={open}
					target={this.targetId}
					toggle={this.closePopover}
					trigger="legacy"
					// Prefer viewport over table-responsive scrollParent so short
					// job lists don't flip placement and put the arrow on the wrong side
					boundariesElement="viewport"
				>
					<PopoverBody style={{ minWidth: '240px', maxWidth: '300px', padding: '10px' }}>
						<div className="d-flex justify-content-between align-items-center mb-2">
							<span className="small text-muted">
								{selected.length} selected
							</span>
							<button
								type="button"
								className="btn btn-link btn-sm p-0"
								onClick={this.clearSelections}
								disabled={selected.length === 0}
							>
								Clear
							</button>
						</div>
						{showSearch && (
							<input
								type="search"
								className="form-control form-control-sm mb-2"
								placeholder="Search values"
								value={search}
								onChange={(event) => this.setState({ search: event.target.value })}
								onClick={(event) => event.stopPropagation()}
							/>
						)}
						<div style={{ maxHeight: '220px', overflowY: 'auto' }}>
							{visibleOptions.length === 0 ? (
								<div className="text-muted small">No values</div>
							) : (
								visibleOptions.map((option) => {
									const checked = selected.includes(String(option.value))
									const optionKey = `${field}-${option.value || NULL_FILTER_VALUE}`
									const count =
										typeof option.count === 'number' ? option.count : null
									const zeroCountTitle =
										count === 0
											? 'No jobs match the current filter selections'
											: undefined
									return (
										<label
											key={optionKey}
											className="d-flex align-items-center mb-1"
											title={zeroCountTitle}
											style={{
												cursor: 'pointer',
												fontWeight: 400,
												gap: '6px',
											}}
										>
											<input
												type="checkbox"
												className="mt-0 mr-1"
												checked={checked}
												aria-label={String(option.label)}
												style={{ cursor: 'pointer', flexShrink: 0 }}
												onChange={() => this.handleToggleValue(option.value)}
												onClick={(event) => event.stopPropagation()}
											/>
											<span
												style={{
													wordBreak: 'break-word',
													flex: 1,
													minWidth: 0,
												}}
											>
												{option.label}
											</span>
											{count !== null && (
												<span
													className="text-muted small ml-2"
													style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
													title={zeroCountTitle}
												>
													{count}
												</span>
											)}
										</label>
									)
								})
							)}
						</div>
						<div className="d-flex justify-content-end mt-2 pt-2 border-top">
							<button
								type="button"
								className="btn btn-primary btn-sm"
								onClick={this.applySelections}
								disabled={!canApply}
							>
								Apply
							</button>
						</div>
					</PopoverBody>
				</Popover>
			</span>
		)
	}
}
